<template>
  <div>
    <header-slot />

    <b-card
      no-body
      class="border-top-primary border-3 border-table-radius px-0"
    >
      <filter-slot
        :filter="filters"
        :filterPrincipal="filterPrincipal"
        :paginate="paginate"
        @reload="refreshTable"
      >
        <template #buttons>
          <div class="d-flex align-items-center flex-wrap">
            <b-form-input
              v-model="mesBase"
              type="month"
              class="mr-1"
              style="width: 165px"
              @change="refreshTable"
            />

            <b-button-group class="mr-1">
              <b-button
                :variant="meses === 1 ? 'primary' : 'outline-primary'"
                @click="cambiarMeses(1)"
              >
                1 mes
              </b-button>
              <b-button
                :variant="meses === 2 ? 'primary' : 'outline-primary'"
                @click="cambiarMeses(2)"
              >
                2 meses
              </b-button>
            </b-button-group>

            <b-button
              v-if="puedeGestionar"
              variant="outline-warning"
              class="mr-1"
              v-b-tooltip.hover.top="
                'Carga inicial del saldo de días libres de cada conductor'
              "
              @click="showSaldoInicial = true"
            >
              <feather-icon icon="UploadIcon" /> Saldo inicial
            </b-button>

            <b-button
              v-if="puedeGestionar"
              variant="outline-secondary"
              class="mr-1"
              v-b-tooltip.hover.top="
                'Congela el saldo del mes para que no se recalcule'
              "
              @click="cerrarPeriodo"
            >
              <feather-icon icon="LockIcon" /> Cerrar mes
            </b-button>

            <b-button
              variant="info"
              v-b-tooltip.hover.top="'Descargar Excel'"
              @click="exportToExcel"
            >
              <feather-icon icon="FileTextIcon" /> Excel
            </b-button>
          </div>
        </template>

        <template #table>
          <div class="d-flex flex-wrap px-1 pt-1 leyenda">
            <span class="mr-2">
              <b-avatar rounded size="18" variant="light-success" /> Día libre
              gozado
            </span>
            <span class="mr-2">
              <b-avatar rounded size="18" variant="light-primary" /> Día libre
              programado
            </span>
            <span class="mr-2">
              <b-avatar rounded size="18" variant="light-warning" /> Día vendido
              (trabaja y se le paga)
            </span>
            <span class="mr-2">
              <b-avatar rounded size="18" variant="light-danger" /> Conflicto:
              vendido y marcado como día libre
            </span>
            <span class="mr-2">
              <b-avatar rounded size="18" variant="light-secondary" /> Otro
              estado de tareo
            </span>
          </div>

          <b-table
            ref="ref-table-dias-libres"
            :per-page="paginate.perPage"
            :current-page="paginate.currentPage"
            sticky-header="60vh"
            small
            class="position-relative font-small-3"
            table-class="text-nowrap"
            :busy.sync="isBusy"
            :items="myProvider"
            :fields="fields"
            primary-key="personal_id"
            :empty-text="'No se encontraron conductores'"
            show-empty
          >
            <template #table-busy>
              <b-skeleton-table
                :rows="paginate.perPage"
                :columns="8"
                :table-props="{ bordered: true, striped: true }"
              />
            </template>

            <!-- Cabecera de las columnas de día -->
            <template #head()="data">
              <div v-if="data.field.dia" class="text-center dia-head">
                <div
                  :class="data.field.es_domingo ? 'text-danger' : 'text-muted'"
                  style="font-size: 0.65rem"
                >
                  {{ data.field.dia_semana }}
                </div>
                <div class="font-weight-bolder">{{ data.field.dia }}</div>
                <div style="font-size: 0.6rem; opacity: 0.6">
                  {{ data.field.mes_label }}
                </div>
              </div>
              <span v-else>{{ data.label }}</span>
            </template>

            <template #cell(personal_name)="data">
              <router-link
                target="_blank"
                class="text-primary"
                :to="{
                  name: 'rrhh-employees-information',
                  params: { id: data.item.personal_id },
                }"
              >
                <feather-icon icon="TruckIcon" size="16" class="text-warning" />
                {{ data.item.personal_name | capitalize }}
              </router-link>
              <br />
              <span style="opacity: 0.6">
                {{ data.item.code }} · {{ data.item.documento }}
              </span>
            </template>

            <template #cell(regimen)="data">
              <b-badge v-if="data.value" variant="light-primary">
                {{ data.value }}
              </b-badge>
              <b-badge
                v-else-if="data.item.regimen_invalido"
                variant="danger"
                v-b-tooltip.hover.top="
                  `El colaborador tiene el régimen ${data.item.regimen_invalido}, que no existe en Ajustes > Tipos de Acuerdo`
                "
              >
                RÉGIMEN INVÁLIDO
              </b-badge>
              <b-badge
                v-else
                variant="light-danger"
                v-b-tooltip.hover.top="
                  'Sin tipo de acuerdo asignado: no genera días libres hasta que RRHH lo configure'
                "
              >
                SIN RÉGIMEN
              </b-badge>
            </template>

            <template #cell(acumulado_anterior)="data">
              <b-badge variant="light-secondary">
                {{ data.value | numeroDias }}
              </b-badge>
            </template>

            <template #cell(meta_rango)="data">
              <b-badge variant="light-info">{{ data.value | numeroDias }}</b-badge>
            </template>

            <template #cell(programados_rango)="data">
              <b-badge variant="light-primary">{{ data.value }}</b-badge>
            </template>

            <template #cell(tomados_rango)="data">
              <b-badge variant="light-success">{{ data.value }}</b-badge>
            </template>

            <template #cell(vendidos_rango)="data">
              <b-badge variant="light-warning">{{ data.value }}</b-badge>
            </template>

            <template #cell(ajustes_rango)="data">
              <b-badge
                :variant="data.value ? 'light-dark' : 'light-secondary'"
                :class="{ 'cursor-pointer': true }"
                @click="verDetalle(data.item)"
              >
                {{ data.value > 0 ? "+" : "" }}{{ data.value | numeroDias }}
              </b-badge>
            </template>

            <template #cell(saldo_actual)="data">
              <b-badge
                :variant="data.value < 0 ? 'danger' : 'success'"
                class="cursor-pointer"
                v-b-tooltip.hover.top="'Ver cómo se formó este saldo'"
                @click="verDetalle(data.item)"
              >
                {{ data.value | numeroDias }}
              </b-badge>
            </template>

            <template #cell(actions)="data">
              <feather-icon
                v-if="puedeGestionar"
                icon="EditIcon"
                size="18"
                class="cursor-pointer text-warning"
                v-b-tooltip.hover.left="'Regularizar saldo'"
                @click="abrirAjuste(data.item)"
              />
            </template>

            <!-- Celdas de día -->
            <template #cell()="data">
              <div
                v-if="data.field.dia"
                class="cursor-pointer position-relative d-inline-block"
                v-b-tooltip.hover.top="tooltipDia(data.value)"
                @click="abrirTareo(data.item, data.field.key)"
              >
                <b-avatar
                  rounded
                  size="30"
                  :variant="variantDia(data.value)"
                  :class="{ 'domingo-cell': data.value.es_domingo }"
                >
                  <span v-if="data.value.abbreviation" style="font-size: 0.7rem">
                    {{ data.value.abbreviation }}
                  </span>
                  <feather-icon v-else icon="MinusIcon" size="12" />
                </b-avatar>
                <feather-icon
                  v-if="data.value.vendido"
                  icon="DollarSignIcon"
                  size="11"
                  class="marca-vendido"
                />
              </div>
            </template>
          </b-table>
        </template>
      </filter-slot>
    </b-card>

    <AjusteDiasLibresModal
      v-if="showAjuste"
      :conductor="conductorSeleccionado"
      :periodo="periodoActual"
      @hidden="showAjuste = false"
      @saved="onAjusteGuardado"
      @refresh="refreshTable"
    />

    <DetalleAcumuladoModal
      v-if="showDetalle"
      :conductor="conductorSeleccionado"
      :periodo="periodoFinal"
      @hidden="showDetalle = false"
    />

    <SaldoInicialModal
      v-if="showSaldoInicial"
      :periodo="periodoActual"
      @hidden="showSaldoInicial = false"
      @saved="onAjusteGuardado"
    />

    <PersonalAttendance
      v-if="showTareo"
      :dataModal="dataModalTareo"
      @hideModal="onTareoCerrado"
    />
  </div>
</template>

<script>
import moment from "moment";
import { mapGetters } from "vuex";

/* Data */
import Fields from "@/views/monitoring/views/dias-libres/data/fields.data.js";
import Filters from "@/views/monitoring/views/dias-libres/data/filters.data.js";

/* Components */
import FilterSlot from "@/views/commons/filter-slot/FilterSlot.vue";
import PersonalAttendance from "@/views/monitoring/views/attendance/components/modals/PersonalAttendance.vue";
import AjusteDiasLibresModal from "@/views/monitoring/views/dias-libres/components/AjusteDiasLibresModal.vue";
import DetalleAcumuladoModal from "@/views/monitoring/views/dias-libres/components/DetalleAcumuladoModal.vue";
import SaldoInicialModal from "@/views/monitoring/views/dias-libres/components/SaldoInicialModal.vue";

/* Services */
import DiasLibresReportService from "@/views/monitoring/views/dias-libres/services/dias-libres-report.service.js";
import ViaticService from "@/views/monitoring/views/viatic/services/viatic.service.js";
import EmployeeService from "@/views/rrhh/views/employees/services/employee.service.js";

export default {
  components: {
    FilterSlot,
    PersonalAttendance,
    AjusteDiasLibresModal,
    DetalleAcumuladoModal,
    SaldoInicialModal,
  },
  filters: {
    numeroDias(value) {
      const numero = Number(value || 0);
      return Number.isInteger(numero) ? numero : numero.toFixed(2);
    },
  },
  data() {
    return {
      filters: Filters,
      fields: [...Fields],
      keys: [],
      isBusy: false,
      mesBase: moment().format("YYYY-MM"),
      meses: 1,
      filterPrincipal: {
        type: "input",
        inputType: "text",
        placeholder: "Buscar conductor, documento o código...",
        model: "",
      },
      paginate: {
        from: 0,
        to: 0,
        of: 0,
        perPage: 25,
        currentPage: 1,
      },
      conductorSeleccionado: {},
      showAjuste: false,
      showDetalle: false,
      showSaldoInicial: false,
      showTareo: false,
      dataModalTareo: {},
    };
  },
  computed: {
    ...mapGetters({
      currentUser: "auth/currentUser",
    }),
    periodoActual() {
      return this.mesBase;
    },
    periodoFinal() {
      return moment(this.mesBase, "YYYY-MM")
        .add(this.meses - 1, "months")
        .format("YYYY-MM");
    },
    puedeGestionar() {
      return this.isCeo || this.isChief;
    },
  },
  created() {
    this.getEntityGroups();
  },
  methods: {
    refreshTable() {
      this.$refs["ref-table-dias-libres"].refresh();
    },

    cambiarMeses(valor) {
      if (this.meses === valor) return;
      this.meses = valor;
      this.refreshTable();
    },

    buildParams(ctx = null) {
      return {
        fecha_base: `${this.mesBase}-01`,
        meses: this.meses,
        entity_group_id: this.filters[0].model,
        solo_con_regimen: this.filters[1].model ? 1 : 0,
        search_txt: this.filterPrincipal.model,
        page: ctx ? ctx.currentPage : this.paginate.currentPage,
        perpage: ctx ? ctx.perPage : this.paginate.perPage,
      };
    },

    async myProvider(ctx) {
      try {
        this.addPreloader();
        const { data } = await DiasLibresReportService.getReporte(
          this.buildParams(ctx)
        );

        this.keys = data.keys;
        this.fields = this.construirFields(data.keys);

        this.paginate.from = data.data.from;
        this.paginate.to = data.data.to;
        this.paginate.of = data.data.total;
        this.paginate.perPage = data.data.per_page;
        this.paginate.currentPage = data.data.current_page;

        return data.data.data || [];
      } catch (error) {
        console.log(error);
        return [];
      } finally {
        this.removePreloader();
      }
    },

    /* Columnas fijas + una columna por cada día del rango */
    construirFields(keys) {
      const fijos = [...Fields];
      const acciones = fijos.pop();

      const dias = keys.map((item) => ({
        key: item.key,
        label: item.dia,
        dia: item.dia,
        dia_semana: item.dia_semana,
        mes_label: item.mes_label,
        es_domingo: item.es_domingo,
        thClass: item.es_inicio_mes ? "text-center inicio-mes" : "text-center",
        tdClass: item.es_inicio_mes ? "text-center inicio-mes" : "text-center",
        thStyle: { minWidth: "44px" },
      }));

      return [...fijos, ...dias, acciones];
    },

    variantDia(celda) {
      if (!celda) return "light-secondary";
      if (celda.conflicto) return "light-danger";
      if (celda.estado === "tomado") return "light-success";
      if (celda.estado === "programado") return "light-primary";
      if (celda.vendido) return "light-warning";
      if (celda.abbreviation) return "light-secondary";
      return "light";
    },

    tooltipDia(celda) {
      if (!celda) return "";
      const partes = [];

      if (celda.conflicto) {
        partes.push("CONFLICTO: día vendido y marcado como día libre");
      } else if (celda.estado === "tomado") {
        partes.push("Día libre gozado");
      } else if (celda.estado === "programado") {
        partes.push("Día libre programado");
      } else if (celda.vendido) {
        partes.push("Día libre vendido: el conductor trabaja y se le paga");
      }

      if (celda.observation && celda.observation !== "null") {
        partes.push(celda.observation);
      }
      if (celda.comment && celda.comment !== "null") {
        partes.push(celda.comment);
      }

      return partes.join(" · ");
    },

    async abrirTareo(item, fecha) {
      if (!this.puedeGestionar) {
        this.showToastWarning("Usuario no autorizado");
        return;
      }

      if (!item.attendance_id) {
        this.showToastWarning(
          "Este conductor aún no tiene tareo aperturado",
          "Sin tareo"
        );
        return;
      }

      try {
        this.addPreloader();
        const celda = item[fecha];
        const { data } = await ViaticService.validateApprovedAttendance({
          date: fecha,
          attendance_id: item.attendance_id,
        });

        this.dataModalTareo = {
          id: item.attendance_id,
          id_detail: celda.id,
          code: item.code,
          personal_name: item.personal_name,
          date: fecha,
          homework_status_id: celda.homework_status_id || null,
          observation: celda.observation || null,
          comment: celda.comment || null,
          origin: "viatic",
          approved: data.isApproved,
          attendance_status_viatic_id: data.att?.attendance_status_viatic_id,
          payroll_id: data.att?.payroll_id,
        };
        this.showTareo = true;
      } catch (error) {
        console.log(error);
      } finally {
        this.removePreloader();
      }
    },

    onTareoCerrado(recargar = false) {
      this.showTareo = false;
      if (recargar) this.refreshTable();
    },

    abrirAjuste(item) {
      this.conductorSeleccionado = item;
      this.showAjuste = true;
    },

    verDetalle(item) {
      this.conductorSeleccionado = item;
      this.showDetalle = true;
    },

    onAjusteGuardado() {
      this.showAjuste = false;
      this.showSaldoInicial = false;
      this.refreshTable();
    },

    async cerrarPeriodo() {
      const { isConfirmed } = await this.showConfirmSwal(
        `¿Cerrar el periodo ${this.periodoActual}?`,
        "El saldo de ese mes queda congelado y dejará de recalcularse aunque después cambie la configuración del régimen."
      );

      if (!isConfirmed) return;

      try {
        this.addPreloader();
        const { data } = await DiasLibresReportService.cerrarPeriodo({
          ...this.buildParams(),
          periodo: this.periodoActual,
          id_usuario: this.currentUser.id,
        });
        this.showToastSuccess(data.message);
        this.refreshTable();
      } catch (error) {
        this.showToastError(
          error.response?.data?.message || 'Ocurrió un error inesperado'
        );
      } finally {
        this.removePreloader();
      }
    },

    async exportToExcel() {
      try {
        this.addPreloader();
        const data = await DiasLibresReportService.exportToExcel(
          this.buildParams()
        );
        this.forceFileDownload(data.data, "reporte-dias-libres.xlsx");
      } catch (error) {
        this.showToastError(
          error.response?.data?.message || 'Ocurrió un error inesperado'
        );
      } finally {
        this.removePreloader();
      }
    },

    async getEntityGroups() {
      try {
        const { data } = await EmployeeService.getEntitiesGroup();
        this.filters[0].options = data;
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>

<style scoped>
.leyenda {
  font-size: 0.75rem;
  opacity: 0.85;
}
.marca-vendido {
  position: absolute;
  top: -4px;
  right: -4px;
  color: #ff9f43;
}
.domingo-cell {
  border: 1px dashed rgba(234, 84, 85, 0.5);
}
</style>

<style>
.inicio-mes {
  border-left: 2px solid rgba(115, 103, 240, 0.5) !important;
}
.dia-head {
  line-height: 1.1;
}
</style>
